export interface TrackedZone {
  zoneIndex: number;
  className: string;
  confidence: number;
  svgX: number;
  svgY: number;
  svgW: number;
  svgH: number;
  firstSeen: number;
  lastSeen: number;
  stale: boolean;
}

export type FreshDetection = Pick<TrackedZone, 'zoneIndex' | 'className' | 'confidence' | 'svgX' | 'svgY' | 'svgW' | 'svgH'>;

export class DetectionTracker {
  private tracked: Map<number, TrackedZone> = new Map();
  private frameCount = 0;
  private persistenceMs: number;
  private persistenceFrames: number;

  constructor(persistenceFrames = 5, persistenceMs = 3000) {
    this.persistenceFrames = persistenceFrames;
    this.persistenceMs = persistenceMs;
  }

  update(freshDetections: FreshDetection[]): TrackedZone[] {
    this.frameCount++;
    const now = Date.now();

    for (const [idx] of this.tracked) {
      this.tracked.get(idx)!.stale = true;
    }

    for (const det of freshDetections) {
      if (this.tracked.has(det.zoneIndex)) {
        const existing = this.tracked.get(det.zoneIndex)!;
        existing.lastSeen = now;
        existing.confidence = det.confidence;
        existing.className = det.className;
        existing.svgX = det.svgX;
        existing.svgY = det.svgY;
        existing.svgW = det.svgW;
        existing.svgH = det.svgH;
        existing.stale = false;
      } else {
        this.tracked.set(det.zoneIndex, {
          ...det,
          firstSeen: now,
          lastSeen: now,
          stale: false,
        });
      }
    }

    return this.getActiveZones();
  }

  getActiveZones(): TrackedZone[] {
    const now = Date.now();
    const result: TrackedZone[] = [];
    for (const zone of this.tracked.values()) {
      const ageMs = now - zone.lastSeen;
      if (!zone.stale) {
        result.push(zone);
      } else if (ageMs < this.persistenceMs) {
        result.push(zone);
      } else {
        this.tracked.delete(zone.zoneIndex);
      }
    }
    return result;
  }

  getFreshZones(): TrackedZone[] {
    return Array.from(this.tracked.values()).filter(z => !z.stale);
  }

  reset(): void {
    this.tracked.clear();
    this.frameCount = 0;
  }
}
